"use client";

/**
 * Mutoni Accessories Hub — site header (navy, matches the hero so there is no
 * hard separator line). Marketing nav (Home / Shop / About) + a prominent
 * "Visit Shop" CTA → the real product shop at /shop, plus a cart icon that
 * opens the shared CartDrawer via the existing cart store. Display-only — the
 * shop owns add-to-cart + checkout.
 */

import { useState } from "react";
import Link from "next/link";
import { ShoppingCart, Menu, X } from "lucide-react";
import { useQuery } from "convex/react";
import { api } from "@/convex/_generated/api";
import { useSession } from "@/lib/auth/auth-client";
import { useCartStore } from "@/lib/store/cartStore";
import type { BrandLite } from "../../types";

const NAV = [
  { label: "Home", href: "/" },
  { label: "Shop", href: "/shop" },
  { label: "About", href: "/about" },
];

export default function MutoniHeader({ brand }: { brand: BrandLite }) {
  const [open, setOpen] = useState(false);
  const openCart = useCartStore((s) => s.openCart);
  const sessionId = useCartStore((s) => s.sessionId);

  // Live cart count — same source as the shop header so the icon stays in sync
  // with the cart drawer / shop. Guests query by sessionId; logged-in users by
  // their account.
  const { data: session, isPending } = useSession();
  const isLoggedIn = !!session?.user;
  const authResolved = !isPending;
  // @ts-ignore - Type instantiation is excessively deep
  const cartItemCount = useQuery(
    api.cart.core.getCartItemCount,
    !authResolved
      ? "skip"
      : isLoggedIn
        ? {}
        : { sessionId: sessionId || undefined },
  );
  const totalItems = cartItemCount ?? 0;

  return (
    <header className="sticky top-0 z-50 bg-[#001233]/85 text-white backdrop-blur-md">
      <div className="mx-auto flex h-16 w-full max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        {/* Brand */}
        <Link href="/" className="flex items-center gap-2.5">
          {brand.profileImage ? (
            // eslint-disable-next-line @next/next/no-img-element
            <img
              src={brand.profileImage}
              alt={brand.name}
              className="h-9 w-9 rounded-full object-cover ring-1 ring-white/20"
            />
          ) : null}
          <span className="text-base font-semibold tracking-tight">
            {brand.name}
          </span>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden items-center gap-8 md:flex">
          {NAV.map((item) => (
            <Link
              key={item.href}
              href={item.href}
              className="text-sm font-medium text-white/70 transition-colors hover:text-white"
            >
              {item.label}
            </Link>
          ))}
        </nav>

        {/* Actions */}
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={openCart}
            aria-label="Open cart"
            className="relative inline-flex h-10 w-10 items-center justify-center rounded-full text-white transition-colors hover:bg-white/10"
          >
            <ShoppingCart className="h-5 w-5" />
            {totalItems > 0 && (
              <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-white px-1 text-[10px] font-bold text-[#001233]">
                {totalItems > 99 ? "99+" : totalItems}
              </span>
            )}
          </button>

          <Link
            href="/shop"
            className="hidden rounded-full bg-white px-5 py-2.5 text-sm font-semibold text-[#001233] transition-transform hover:scale-[1.03] sm:inline-flex"
          >
            Visit Shop
          </Link>

          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
            className="inline-flex h-10 w-10 items-center justify-center rounded-full text-white transition-colors hover:bg-white/10 md:hidden"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="border-t border-white/10 bg-[#001233] md:hidden">
          <nav className="mx-auto flex w-full max-w-7xl flex-col gap-1 px-4 py-3">
            {NAV.map((item) => (
              <Link
                key={item.href}
                href={item.href}
                onClick={() => setOpen(false)}
                className="rounded-lg px-3 py-2.5 text-sm font-medium text-white/85 transition-colors hover:bg-white/10"
              >
                {item.label}
              </Link>
            ))}
            <Link
              href="/shop"
              onClick={() => setOpen(false)}
              className="mt-1 rounded-lg bg-white px-3 py-2.5 text-center text-sm font-semibold text-[#001233]"
            >
              Visit Shop
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}
